import { useCallback, useEffect, useState, useRef } from 'react'
import type { ChangeEvent } from 'react'

import { useTranslation } from 'react-i18next'

import { Button } from 'components/Button'
import { Heading } from 'components/Typography'
import { setAdminMode, getAdminMode } from 'storage'
import getEnv from 'utils/getEnv'

import { Switch } from '../../components/Switch'
import { Modal, ModalBody } from '../../layouts/Modal/'

import {
  SettingsFooter,
  SettingsContainer,
  DanceSprite,
  TitleBlock,
} from './styles'

const Settings = (): JSX.Element => {
  const { VITE_APP_VERSION } = getEnv()
  const [modeChecked, setModeChecked] = useState<boolean>(
    getAdminMode() || false,
  )
  const { t } = useTranslation()

  const onModeSelect = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target
    setModeChecked(checked)
    setAdminMode(checked)
  }, [])

  const clearServiceWorkerCache = async () => {
    /** Unregister the service worker before clearing caches  */
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      registrations.forEach((registration) => {
        console.log('Unregistering service worker', registration)
        registration.unregister()
      })
    }

    if ('caches' in window) {
      const cacheNames = await caches.keys()
      console.log('Clearing caches', cacheNames)
      await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)))
    }
  }

  const clearCaches = async () => {
    await clearServiceWorkerCache()
    localStorage.clear()
    window.location.reload()
  }

  return (
    <SettingsContainer>
      <TitleBlock>
        <Heading size="xLarge">{t('settings.title')}</Heading>
        <DanceSprite />
      </TitleBlock>
      <Switch
        key="mode"
        name="mode"
        onChange={onModeSelect}
        checked={modeChecked}
      >
        {t('settings.admin-label')}
      </Switch>

      <SettingsFooter>
        <span>
          {t('settings.version')}: {VITE_APP_VERSION}
          <br />
          {t('settings.admin-label-short')}: {getAdminMode() ? 'ON' : 'OFF'}
        </span>
        <Button onClick={clearCaches}>Clear Caches</Button>
      </SettingsFooter>
    </SettingsContainer>
  )
}

const TAP_TIMEOUT = 300
const TAP_COUNT = 3
const TAP_FINGERS = 3
const TAP_DEBOUNCE = 50

export const SettingsModal = () => {
  const [showSettings, setShowSettings] = useState(false)
  const [_, setTapCount] = useState<number>(0)

  const tapTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  const onClose = () => {
    setShowSettings(false)
  }

  useEffect(() => {
    const handleTouchStart = (event: TouchEvent) => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current)

      debounceTimeout.current = setTimeout(() => {
        if (event.touches.length === TAP_FINGERS) {
          setTapCount((prevCount) => {
            const count = prevCount + 1
            if (tapTimeout.current) clearTimeout(tapTimeout.current)
            tapTimeout.current = setTimeout(() => {
              setTapCount(0)
            }, TAP_TIMEOUT)

            if (count === TAP_COUNT) {
              setShowSettings(true)
              return 0
            }

            return count
          })
        }
      }, TAP_DEBOUNCE)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.altKey && event.code === 'KeyD') {
        setShowSettings(true)
      }
    }

    document.addEventListener('touchstart', handleTouchStart)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      if (tapTimeout.current) clearTimeout(tapTimeout.current)
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current)
      document.removeEventListener('touchstart', handleTouchStart)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  return (
    <Modal show={showSettings} onClose={onClose} closeTitle="Close Settings">
      <ModalBody>
        <Settings />
      </ModalBody>
    </Modal>
  )
}
